import styled, { css } from 'styled-components'

import { isConsole } from '../../config/config'
import { useNotes } from '../../hooks/query/useNotes'
import { ApiVersion } from '../../models'
import { isNote, NoteOrActivity, NoteRecordType, RecordInlineLabel } from '../../models/note'
import { lightGray2 } from '../../theme/colors'
import { Button } from '../Button/Button'
import { Text } from '../Text/Text'
import { ActivityItem } from './ActivityItem'
import { NoteItem } from './NoteItem'
import { NOTES_GAP } from './Notes.constants'

interface NotesListMessagesProps {
  recordId: string
  recordType: NoteRecordType
  isTimeline?: boolean
  showOnlyActivity?: boolean
  notes?: NoteOrActivity[]
  filterTags: string[] | null
  handleDeleteNote: (noteId: string) => Promise<void>
  handleUpdateNote: (noteId: string, text: string, tags?: string[]) => Promise<void>
  apiVersion?: ApiVersion
  isStatic?: boolean
}

export function NotesListMessages({
  recordType,
  recordId,
  isTimeline,
  filterTags,
  showOnlyActivity,
  handleDeleteNote,
  handleUpdateNote,
  notes,
  apiVersion,
  isStatic,
}: NotesListMessagesProps) {
  const { hasNextPage, fetchNextPage, isFetching } = useNotes(
    { recordType, recordId, isTimeline, apiVersion },
    { enabled: false }
  )

  if (!notes?.length)
    return (
      <Wrap>
        <Text variant="label2" fontColor={lightGray2} mt={2}>
          {filterTags?.length ? (
            <>No notes matching your filters</>
          ) : (
            <>
              Add a note about {isConsole() ? 'the' : 'your'} {RecordInlineLabel[recordType]}.
            </>
          )}
        </Text>
        {!isConsole() && !filterTags?.length && (
          <Text variant="label2" fontColor={lightGray2} mt={1}>
            Notes are only visible to your fleet admins.
          </Text>
        )}
      </Wrap>
    )

  return (
    <Wrap $isStatic={isStatic} className="notes-list">
      {notes.map((note) =>
        isNote(note) ? (
          showOnlyActivity ? null : (
            <NoteItem
              key={note.id}
              note={note}
              onDelete={handleDeleteNote}
              onEdit={handleUpdateNote}
              recordId={recordId}
              recordType={recordType}
              isStatic={isStatic}
            />
          )
        ) : (
          <ActivityItem key={note.dateStart} activity={note} />
        )
      )}

      {hasNextPage && (
        <Button
          onClick={() => fetchNextPage()}
          variant="primary"
          size="small"
          margin="0 auto 20px"
          isLoading={isFetching}
        >
          Load more
        </Button>
      )}
    </Wrap>
  )
}

const Wrap = styled.div<{ $isStatic?: boolean }>`
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 ${NOTES_GAP}px;
  margin: 0 -${NOTES_GAP}px;

  ${(p) =>
    p.$isStatic &&
    css`
      padding: 0 10px 0 0;
      margin: 0;
    `}
`
